import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Carousel, Spinner, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaShoppingCart, FaBook, FaLaptop, FaTshirt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import ProductCard from '../components/ProductCard';
import Layout from '../components/layout/Layout';
import api from '../services/api';
// Removed device detection import - using pure CSS solution
import './Home.css';

// Import slider images
import slider1 from '../assets/1.png';
import slider2 from '../assets/2.png';
import slider3 from '../assets/3.png';
import slider4 from '../assets/4.png';
import slider5 from '../assets/5.png';
import { ghanaUniversities } from '../data/universities';

const CATEGORIES = [
  { id: 1, name: 'Textbooks', icon: '📚' },
  { id: 2, name: 'Electronics', icon: '💻' },
  { id: 3, name: 'Stationery', icon: '✏️' },
  { id: 4, name: 'Furniture', icon: '🪑' },
  { id: 5, name: 'Clothing', icon: '👕' },
  { id: 6, name: 'Food', icon: '🍔' }
];

const Home = () => {
  const { currentUser, userRole } = useAuth();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUniversity, setSelectedUniversity] = useState('All');
  const [universities, setUniversities] = useState([]);
  
  // Using pure CSS solution for responsiveness - no JavaScript needed

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        let queryParams = '';
        if (selectedUniversity !== 'All') {
          queryParams = `?university=${encodeURIComponent(selectedUniversity)}`;
        }
        const [featuredRes, recentRes] = await Promise.all([
          api.get(`/products/featured${queryParams}`),
          api.get(`/products/recent${queryParams}`)
        ]);

        const featuredData = Array.isArray(featuredRes.data?.results)
          ? featuredRes.data.results
          : (Array.isArray(featuredRes.data) ? featuredRes.data : []);

        const recentData = Array.isArray(recentRes.data?.results)
          ? recentRes.data.results
          : (Array.isArray(recentRes.data) ? recentRes.data : []);

        setFeaturedProducts(featuredData);
        setRecentProducts(recentData);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedUniversity]);

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        // Use the imported ghanaUniversities as a fallback
        setUniversities(ghanaUniversities);
        
        // Try to fetch from API
        const response = await api.get('/users/universities/');
        if (Array.isArray(response.data?.results)) {
          setUniversities(response.data.results);
        } else if (Array.isArray(response.data)) {
          setUniversities(response.data);
        }
      } catch (error) {
        console.error('Error fetching universities:', error);
      }
    };
    
    fetchUniversities();
  }, []);

  const renderMobileView = () => (
    <section className="hero-section-container mb-5">
      <Container>
        <Row className="justify-content-center">
          <Col lg={8} xl={6}>
            <div className="hero-panel welcome-panel h-100">
              <h2>Welcome to UniTrade</h2>
              <p>Buy and sell products within your university community.</p>
              <div>
                <Button as={Link} to="/products" variant="primary" className="me-2">Browse Products</Button>
                {!currentUser && (
                  <Button as={Link} to="/register" variant="light">Join Now</Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );

  const renderDesktopView = () => (
    <section className="hero-section">
      <Carousel fade interval={5000} className="hero-carousel">
        <Carousel.Item>
          <div className="carousel-image-container">
            <img
              className="carousel-image"
              src={slider1}
              alt="Welcome to UniTrade"
            />
          </div>
          <Carousel.Caption className="carousel-caption">
            <h2>Welcome to UniTrade</h2>
            <p>Buy and sell products within your university community</p>
            <Button as={Link} to="/products" variant="primary">Browse Products</Button>
          </Carousel.Caption>
        </Carousel.Item>
        <Carousel.Item>
          <div className="carousel-image-container">
            <img
              className="carousel-image"
              src={slider2}
              alt="Textbooks for Sale"
            />
          </div>
          <Carousel.Caption className="carousel-caption">
            <h2>Textbooks for Sale</h2>
            <p>Find your required course materials at student-friendly prices</p>
            <Button as={Link} to="/products?category=Textbooks" variant="primary">Find Textbooks</Button>
          </Carousel.Caption>
        </Carousel.Item>
      </Carousel>
    </section>
  );

  const { isMobileMenuOpen } = useMobileMenu();

  return (
    <Layout>
      {/* Hero Section - Both views rendered, CSS controls visibility */}
      <div className={`banner-wrapper ${isMobileMenuOpen ? 'menu-open' : ''}`}>
        {renderMobileView()}
        {renderDesktopView()}
      </div>
      
      <Container fluid className="home-container" style={{ position: 'relative' }}>
        {/* Categories Section */}
        <section className="py-5 categories-section">
          <Container>
            <h2 className="section-title">Browse Categories</h2>
            <Row>
              {CATEGORIES.map(category => (
                <Col key={category.id} xs={6} md={4} lg={2} className="mb-4">
                  <Link to={`/products?category=${category.name}`} className="text-decoration-none">
                    <Card className="category-card">
                      <Card.Body>
                        <div className="fs-1 mb-2">{category.icon}</div>
                        <Card.Title className="fs-6">{category.name}</Card.Title>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Featured Products Section */}
        <section className="py-5 bg-light">
          <Container>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="section-title mb-0">Featured Products</h2>
              <Form.Select 
                style={{ width: 'auto' }} 
                value={selectedUniversity}
                onChange={(e) => setSelectedUniversity(e.target.value)}
              >
                <option value="All">All Universities</option>
                {universities.map(uni => (
                  <option key={uni.id || uni.name} value={uni.name}>{uni.name}</option>
                ))}
              </Form.Select>
            </div>
            {loading ? (
              <div className="text-center"><Spinner animation="border" variant="primary" /></div>
            ) : (
              <Row>
                {featuredProducts.map(product => (
                  <Col key={product.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                    <ProductCard product={product} />
                  </Col>
                ))}
              </Row>
            )}
            <div className="text-center mt-3">
              <Button as={Link} to="/products" variant="primary">View All Products</Button>
            </div>
          </Container>
        </section>

        {/* Recent Products Section */}
        <section className="py-5">
          <Container>
            <h2 className="section-title">Recently Added</h2>
            {loading ? (
              <div className="text-center"><Spinner animation="border" variant="primary" /></div>
            ) : (
              <Row>
                {recentProducts.map(product => (
                  <Col key={product.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                    <ProductCard product={product} />
                  </Col>
                ))}
              </Row>
            )}
          </Container>
        </section>
      </Container>
    </Layout>
  );
};

export default Home;
